import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import {
    Hammer,
    Wrench,
    Lightbulb,
    Key,
    Flower2,
    Home,
    ShieldCheck,
    Paintbrush,
    Settings,
    LayoutDashboard
} from "lucide-react";

const PHRASES = [
    "Preparing Your Home",
    "Polishing the Appliances",
    "Checking the Plants",
    "Organizing Your Home",
    "Scheduling Maintenance",
    "Setting Up Your Spaces",
    "Syncing Home Data"
];

const LoadingScreen = () => {
    const [phraseIndex, setPhraseIndex] = useState(0);
    const isWebsite = typeof window !== 'undefined' && window.location.pathname === '/site';

    useEffect(() => {
        if (isWebsite) return;
        const interval = setInterval(() => {
            setPhraseIndex((prev) => (prev + 1) % PHRASES.length);
        }, 1500);
        return () => clearInterval(interval);
    }, [isWebsite]);

    // Floating items configuration
    const floatingItems = [
        { Icon: Key, delay: 0, x: -120, y: -150, rotate: 15 },
        { Icon: Hammer, delay: 0.5, x: 140, y: -100, rotate: -20 },
        { Icon: Flower2, delay: 1, x: -150, y: 120, rotate: 10 },
        { Icon: Lightbulb, delay: 1.5, x: 130, y: 150, rotate: -15 },
        { Icon: ShieldCheck, delay: 2, x: 180, y: 0, rotate: 5 },
        { Icon: Wrench, delay: 2.5, x: -180, y: -20, rotate: -10 },
        { Icon: Paintbrush, delay: 1.2, x: 50, y: -180, rotate: 25 },
        { Icon: Settings, delay: 1.8, x: -60, y: 180, rotate: -30 },
    ];

    return (
        <motion.div
            initial={{ opacity: 1 }}
            exit={{
                opacity: 0,
                scale: 1.05,
                filter: "blur(10px)",
                transition: { duration: 0.4, ease: "easeInOut" }
            }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-white overflow-hidden"
        >
            <div className="relative w-full h-full flex items-center justify-center">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(var(--primary-rgb),0.05)_0%,transparent_70%)]" />

                {/* Floating Items Container */}
                {!isWebsite && (
                    <div className="absolute flex items-center justify-center w-64 h-64">
                        {floatingItems.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{
                                    opacity: [0, 0.4, 0.4, 0],
                                    scale: [0.5, 1, 1, 0.5],
                                    x: [item.x, item.x * 1.5],
                                    y: [item.y, item.y * 1.5],
                                    rotate: [item.rotate, item.rotate * 2]
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    delay: item.delay,
                                    ease: "easeInOut"
                                }}
                                className="absolute text-primary/30"
                            >
                                <item.Icon size={32} strokeWidth={1.5} />
                            </motion.div>
                        ))}
                    </div>
                )}

                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                        duration: 1,
                        ease: [0.23, 1, 0.32, 1],
                    }}
                    className="relative z-10 flex flex-col items-center"
                >
                    <div className="flex flex-col items-center gap-8">
                        <Logo size="xl" className="scale-110 md:scale-125" loading={true} />

                        <div className="flex flex-col items-center gap-4 min-h-[60px]">
                            {!isWebsite && (
                                <AnimatePresence mode="wait">
                                    <motion.p
                                        key={phraseIndex}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.5 }}
                                        className="text-[11px] tracking-[0.4em] text-primary/60 font-bold uppercase"
                                    >
                                        {PHRASES[phraseIndex]}
                                    </motion.p>
                                </AnimatePresence>
                            )}

                            {/* Refined Progress bar */}
                            <div className="w-32 h-1 bg-gray-50 rounded-full overflow-hidden mt-2 border border-gray-100/50 shadow-inner">
                                <motion.div
                                    className="h-full bg-primary/40"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 3, ease: "easeInOut" }}
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default LoadingScreen;

